import { OptimizerRunV2Mode, OptimizerRunV2State } from '@generated/graphql';
import { getOrganizationUserName } from '@interfaces/organizationUser';
import type { TFunction } from 'i18next';
import type { BulkRebalanceRun } from '../../types/run.types';
import { formatRunOutcome, formatRunProgressOutcome } from './formatRunSummary';
import { isRunInProgress } from './runProgress';

export const DEFAULT_RUN_ACTION_HINT_KEY = 'actionHint.reviewResults';

export const formatRunCreatedBy = (run: BulkRebalanceRun): string | null => {
  const name = getOrganizationUserName(run.createdBy ?? undefined);

  return name.length > 0 ? name : null;
};

export const formatRunOrganization = (
  translateBackendString: (translatedObject: unknown) => string,
  run: BulkRebalanceRun,
): string | null => {
  const name = translateBackendString(run.organization?.translatedName);

  return name.length > 0 ? name : null;
};

export const getRunActionHintKey = (run: BulkRebalanceRun): string => {
  if (isRunInProgress(run.state)) {
    return 'actionHint.running';
  }

  if (run.state === OptimizerRunV2State.Failed) {
    return 'actionHint.failed';
  }

  if (run.state === OptimizerRunV2State.Completed) {
    if (run.mode === OptimizerRunV2Mode.DryRun) {
      return 'actionHint.preview';
    }

    return DEFAULT_RUN_ACTION_HINT_KEY;
  }

  return 'actionHint.review';
};

/** Primary outcome line for the run history table. */
export const formatRunListOutcomePrimary = (
  t: TFunction,
  run: BulkRebalanceRun,
): string => {
  if (isRunInProgress(run.state)) {
    return String(formatRunProgressOutcome(t, run.portfolioSummary));
  }

  return String(formatRunOutcome(t, run.portfolioSummary));
};

/** Secondary outcome lines — non-default action hints only. */
export const formatRunListOutcomeSecondaryLines = (
  t: TFunction,
  run: BulkRebalanceRun,
): string[] => {
  const actionKey = getRunActionHintKey(run);

  if (actionKey === DEFAULT_RUN_ACTION_HINT_KEY) {
    return [];
  }

  return [t(actionKey)];
};
